import useSWR from 'swr';

import { Pack } from '@/components/app/cards/PacksList/types';
import { useApi } from '@/hooks/api';
import { useUserState } from '@/hooks/store';

interface PacksClassResponse {
	pack: Pack;
}

interface PacksClassReturn {
	isLoading: boolean;
	pack?: Pack | null;
	title?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors the untyped `error` SWR already surfaces in usePacksFeed
	error: any;
	hasResponse: boolean;
	notFound: boolean;
}

export function usePacksClass(packClassName?: string | null): PacksClassReturn {
	const apiRequest = useApi();
	const user = useUserState();

	const { data, isValidating, error } = useSWR<Pack>(
		[packClassName ? `packs/class/${packClassName}` : null, packClassName, user?.id],
		async ([endpoint]: [endpoint: string]) => {
			const res = await apiRequest<PacksClassResponse>({
				endpoint,
				method: 'GET',
				customHeaders: {
					'x-device-capabilities': 'v1;signedCookie;streamOnly',
				},
			});
			return res.data.pack;
		},
		{ revalidateOnFocus: false, errorRetryCount: 0 },
	);

	return {
		isLoading: isValidating,
		pack: data ?? null,
		title: data?.title,
		error,
		// Positive confirmation that a payload came back, which `error` alone does not give:
		// the shared axios interceptor rejects on an HTTP status >= 400 and on a
		// network-level failure (DNS, timeout, reset — no response at all), but a malformed
		// 200 resolves with no error and nothing usable either. A caller distinguishing
		// "this class is really empty" from "nothing usable came back" has to check this,
		// not just `error`.
		hasResponse: data !== undefined,
		// A 404 is the server actively confirming this class doesn't exist — the one error
		// shape that should override the "don't trust it" caution above and redirect anyway.
		// Every other error (5xx, or a network-level failure with no status at all) stays
		// unconfirmed, since neither means the class itself doesn't exist.
		notFound: error?.status === 404,
	};
}
